// src/context/PermissionContext.jsx
import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    useCallback,
    useMemo,
} from 'react';
import axios from 'axios';
import { API_BASE_URL_SECURITY } from '../utils/config';
import { AuthContext } from './AuthContext';
import { AuthSecurityContext } from './AuthSecurityContext';
import { PERMISSION_MAP } from './Mappings/permissionMap';

export const PermissionContext = createContext();

/* =========================
   Helpers de validación
   ========================= */
const buildPermissionCatalog = () => {
    const moduleSet = new Set();
    const subMap = new Map(); // moduleKey -> Set(submoduleKeys)

    (PERMISSION_MAP || []).forEach((m) => {
        if (!m?.moduleKey) return;
        moduleSet.add(m.moduleKey);
        const subs = new Set();
        (m.submodules || []).forEach((s) => {
            if (s?.submoduleKey) subs.add(s.submoduleKey);
        });
        subMap.set(m.moduleKey, subs);
    });

    return { moduleSet, subMap };
};

const normalizeEntry = (raw) => {
    const moduleKey = typeof raw?.moduleKey === 'string' ? raw.moduleKey.trim() : '';
    const hasSub = raw && Object.prototype.hasOwnProperty.call(raw, 'submoduleKey');
    const subRaw = hasSub ? raw.submoduleKey : undefined;
    const submoduleKey =
        subRaw === null || typeof subRaw === 'undefined'
            ? null
            : typeof subRaw === 'string'
                ? subRaw.trim()
                : null;

    const canView = !!raw?.canView;
    return { moduleKey, submoduleKey, canView };
};

const validateAgainstMap = (entry, moduleSet, subMap) => {
    if (!moduleSet.has(entry.moduleKey)) return false;
    if (entry.submoduleKey === null) return true; // nivel módulo
    const allowedSubs = subMap.get(entry.moduleKey) || new Set();
    return allowedSubs.has(entry.submoduleKey);
};

const dedupePermissions = (list) => {
    // Combina duplicados por (moduleKey, submoduleKey) haciendo OR de canView
    const map = new Map();
    list.forEach((p) => {
        const key = `${p.moduleKey}::${p.submoduleKey === null ? 'null' : p.submoduleKey}`;
        const prev = map.get(key);
        if (!prev) {
            map.set(key, p);
        } else {
            map.set(key, { ...prev, canView: !!(prev.canView || p.canView) });
        }
    });
    return Array.from(map.values());
};

const normalizeAndValidatePermissions = (rawList) => {
    const { moduleSet, subMap } = buildPermissionCatalog();
    const normalized = Array.isArray(rawList) ? rawList.map(normalizeEntry) : [];
    const filtered = normalized.filter((n) => validateAgainstMap(n, moduleSet, subMap));
    return dedupePermissions(filtered);
};

/* =========================
   Caché local por usuario
   ========================= */
const cacheKey = (userId) => `permCache/v1/${userId}`;

const readCache = (userId) => {
    try {
        const raw = localStorage.getItem(cacheKey(userId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const writeCache = (userId, perms) => {
    try {
        localStorage.setItem(cacheKey(userId), JSON.stringify(perms || []));
    } catch {
        /* noop */
    }
};

/* =========================
   Derivación robusta de userId
   ========================= */
const deriveLocalUserId = () => {
    try {
        const raw = localStorage.getItem('userData');
        if (!raw) return null;
        const u = JSON.parse(raw);
        return (
            u?._id ||
            u?.id ||
            u?.userId ||
            u?.USER_ID ||
            u?.user?._id ||
            u?.user?.id ||
            u?.user?.userId ||
            u?.user?.USER_ID ||
            null
        );
    } catch {
        return null;
    }
};

const getUserIdFromAny = (u) => {
    if (!u) return null;
    return (
        u?._id ||
        u?.id ||
        u?.userId ||
        u?.USER_ID ||
        u?.user?._id ||
        u?.user?.id ||
        u?.user?.userId ||
        u?.user?.USER_ID ||
        null
    );
};

/* =========================
   Provider
   ========================= */
export const PermissionProvider = ({ children }) => {
    // ✅ AuthContext realmente expone userData (y ahora también user)
    const { userData: appUserData, user: appUserAlias } = useContext(AuthContext);
    const { user: securityUser } = useContext(AuthSecurityContext);

    const appUser = appUserData || appUserAlias || null;

    const [permissions, setPermissions] = useState([]); // [{moduleKey, submoduleKey, canView}]
    const [loading, setLoading] = useState(true);       // carga inicial (sin datos aún)
    const [refreshing, setRefreshing] = useState(false); // refresco background
    const [showPermissionUI, setShowPermissionUI] = useState(false);

    // ✅ userId de localStorage, pero REACTIVO (antes estaba memoizado y se quedaba null)
    const [localUserId, setLocalUserId] = useState(() => deriveLocalUserId());

    useEffect(() => {
        // Cada vez que cambia el usuario (login/logout), re-derivamos por si el provider ya estaba montado
        setLocalUserId(deriveLocalUserId());
    }, [appUser, securityUser]);

    const currentUserId = useMemo(() => {
        return (
            getUserIdFromAny(appUser) ||
            getUserIdFromAny(securityUser) ||
            localUserId ||
            null
        );
    }, [appUser, securityUser, localUserId]);

    // ¿Se requieren permisos iniciales?
    const needsPermissionSetup = useMemo(() => {
        return !loading && !refreshing && Array.isArray(permissions) && permissions.length === 0;
    }, [permissions, loading, refreshing]);

    /* =========================
       Fetch con timeout + reintentos/backoff
       ========================= */
    const fetchPermissionsOnce = useCallback(async (userId) => {
        const url = `${API_BASE_URL_SECURITY}permissions/${userId}`;

        // ✅ Evita requests colgadas que dejan el loader infinito
        const res = await axios.get(url, { timeout: 10000 });

        // soporta ambas formas: [] o { permissions: [] }
        const raw = res?.data?.permissions ?? res?.data ?? [];
        return normalizeAndValidatePermissions(raw);
    }, []);

    const fetchPermissionsWithRetry = useCallback(
        async (userId, attempts = 3) => {
            let error;
            let lastValid = null;

            for (let i = 0; i < attempts; i++) {
                try {
                    const list = await fetchPermissionsOnce(userId);

                    // Rechazar vacíos en intentos tempranos para evitar “carrera” (tu caso de refresh arregla al recargar)
                    if (list.length === 0 && i < attempts - 1) {
                        await new Promise((r) => setTimeout(r, 250 * Math.pow(3, i)));
                        continue;
                    }

                    lastValid = list;
                    break;
                } catch (e) {
                    error = e;
                    await new Promise((r) => setTimeout(r, 250 * Math.pow(3, i)));
                }
            }

            if (lastValid) return lastValid;
            if (error) throw error;
            return [];
        },
        [fetchPermissionsOnce]
    );

    /* =========================
       Carga de permisos (cache-first + no loader infinito)
       ========================= */
    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            // ✅ Si todavía no hay userId, NO nos quedamos pegados en loading=true
            if (!currentUserId) {
                if (!cancelled) {
                    setPermissions([]);
                    setLoading(false);
                    setRefreshing(false);
                }
                return;
            }

            // 1) Cache-first: si hay caché, mostramos UI ya (sin loader)
            const cached = readCache(currentUserId);
            if (!cancelled) {
                if (cached && cached.length > 0) {
                    setPermissions(cached);
                    setLoading(false); // ✅ ya podemos renderizar
                    setRefreshing(true);
                } else {
                    // sin caché: sí mostramos loader inicial
                    setLoading(true);
                    setRefreshing(true);
                    setPermissions([]); // reset por cambio de usuario
                }
            }

            // 2) Luego refrescamos desde red (en background si hubo caché)
            try {
                const fresh = await fetchPermissionsWithRetry(currentUserId, 3);
                if (!cancelled) {
                    setPermissions(fresh);
                    writeCache(currentUserId, fresh);
                }
            } catch {
                // Si falla, nos quedamos con caché (si existía) o con []
                if (!cancelled) {
                    setPermissions((prev) => (Array.isArray(prev) ? prev : []));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setRefreshing(false);
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [currentUserId, fetchPermissionsWithRetry]);

    /* =========================
       Helper de autorización
       ========================= */
    const allow = useCallback(
        (moduleKey, submoduleKey = null) => {
            if (!Array.isArray(permissions) || permissions.length === 0) return false;

            if (submoduleKey === null) {
                // Registro explícito a nivel módulo
                const hasModuleRecord = permissions.some(
                    (p) =>
                        p.moduleKey === moduleKey &&
                        (p.submoduleKey === null || typeof p.submoduleKey === 'undefined') &&
                        p.canView === true
                );
                if (hasModuleRecord) return true;

                // Compatibilidad: si al menos un submódulo está activo, habilitamos el módulo
                const anySubEnabled = permissions.some(
                    (p) =>
                        p.moduleKey === moduleKey &&
                        p.submoduleKey != null &&
                        p.canView === true
                );
                return anySubEnabled;
            }

            // Submódulo específico
            return permissions.some(
                (p) =>
                    p.moduleKey === moduleKey &&
                    p.submoduleKey === submoduleKey &&
                    p.canView === true
            );
        },
        [permissions]
    );

    /* =========================
       Árbol de permisos para UIs
       ========================= */
    const permissionTree = useMemo(() => {
        return (PERMISSION_MAP || []).map((m) => {
            const moduleAllowed = allow(m.moduleKey, null);
            const subs = (m.submodules || []).map((s) => ({
                ...s,
                allowed: allow(m.moduleKey, s.submoduleKey),
            }));
            return { ...m, allowed: moduleAllowed, submodules: subs };
        });
    }, [allow]);

    /* =========================
       Acciones
       ========================= */
    const openPermissionUI = useCallback(() => setShowPermissionUI(true), []);
    const closePermissionUI = useCallback(() => setShowPermissionUI(false), []);

    const savePermissions = useCallback(
        async (draft) => {
            if (!currentUserId) return;
            const url = `${API_BASE_URL_SECURITY}permissions/${currentUserId}`;
            await axios.put(url, { permissions: draft }, { timeout: 15000 });

            // Refrescamos desde backend para asegurar consistencia
            setRefreshing(true);
            try {
                const fresh = await fetchPermissionsWithRetry(currentUserId, 3);
                setPermissions(fresh);
                writeCache(currentUserId, fresh);
            } finally {
                setRefreshing(false);
            }
        },
        [currentUserId, fetchPermissionsWithRetry]
    );

    const refreshPermissions = useCallback(async () => {
        if (!currentUserId) return;
        setRefreshing(true);
        try {
            const fresh = await fetchPermissionsWithRetry(currentUserId, 3);
            setPermissions(fresh);
            writeCache(currentUserId, fresh);
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, [currentUserId, fetchPermissionsWithRetry]);

    const value = {
        loading,
        refreshing,
        permissions,
        setPermissions,
        allow,
        permissionTree,
        needsPermissionSetup,
        showPermissionUI,
        openPermissionUI,
        closePermissionUI,
        savePermissions,
        refreshPermissions,
    };

    return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};
